import React from 'react';

import { isValidNumber, roundToDecimals } from '../../../../../utils/math/number';
import { isDefined } from '../../../../../utils/variables';
import { Point3d } from '../../../../../viewer/utilities/math-helpers';
import { LabelledContainer } from '../../../labelled-container/labelled-container';
import { Stack } from '../../../stack/stack';
import { TextBox } from '../../../text-box/text-box';

type Props = {
  display?: [string, string] | [string, string, string];
  defaultValue?: Point3d;
  disabled?: boolean;
  onValueChanged: (point: Point3d) => void;
};

export const PositionInput = (props: Props) => {
  const [point, setPoint] = React.useState<Point3d>(props.defaultValue ?? { x: 0, y: 0, z: 0 });
  const [x, setX] = React.useState(roundToDecimals(props.defaultValue?.x ?? 0, 3).toString());
  const [y, setY] = React.useState(roundToDecimals(props.defaultValue?.y ?? 0, 3).toString());
  const [z, setZ] = React.useState(roundToDecimals(props.defaultValue?.z ?? 0, 3).toString());

  const labels = props.display ?? ['X', 'Y', 'Z'];

  const onValueChanged = (
    newValue: string,
    setCardinal: (val: string) => void,
    key: keyof Point3d,
  ) => {
    const value = Number(newValue);
    if (isValidNumber(value)) {
      const newPoint = { ...point };
      newPoint[key] = value;
      setCardinal(newValue);
      setPoint(newPoint);
      props.onValueChanged(newPoint);
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const data = e.clipboardData?.getData('text');

    if (!isDefined(data)) {
      return;
    }

    if (isValidNumber(Number(data))) {
      return;
    }

    const separators = ['\t', ';', ' ', ','];

    for (const seperator of separators) {
      const values = data.split(seperator);

      const parsedValues: number[] = [];

      for (const value of values) {
        const n = Number(value);
        if (isValidNumber(n)) {
          parsedValues.push(n);
        }
      }

      // If there are at least 2 parsed values, set both X and Y.
      if (parsedValues.length >= 2) {
        e.preventDefault();

        setX(parsedValues[0].toFixed(2));
        setY(parsedValues[1].toFixed(2));

        // If there are at least 3 values, set Z too.
        if (parsedValues.length > 2) {
          setZ(parsedValues[2].toFixed(2));
        }

        props.onValueChanged({ x: parsedValues[0], y: parsedValues[1], z: parsedValues[2] ?? 0 });

        break;
      }
    }
  };

  return (
    <Stack spacing={0.5}>
      <LabelledContainer text={labels[0]}>
        {(formElementId) => (
          <TextBox
            disabled={props.disabled}
            disableScroll={true}
            hideStepper={true}
            id={formElementId()}
            step={0.01}
            type="number"
            value={x}
            onChange={(e) => onValueChanged(e.target.value, setX, 'x')}
            onPaste={onPaste}
          />
        )}
      </LabelledContainer>
      <LabelledContainer text={labels[1]}>
        {(formElementId) => (
          <TextBox
            disabled={props.disabled}
            disableScroll={true}
            hideStepper={true}
            id={formElementId()}
            step={0.01}
            type="number"
            value={y}
            onChange={(e) => onValueChanged(e.target.value, setY, 'y')}
            onPaste={onPaste}
          />
        )}
      </LabelledContainer>
      {labels.length === 3 && (
        <LabelledContainer text={labels[2]}>
          {(formElementId) => (
            <TextBox
              disabled={props.disabled}
              disableScroll={true}
              hideStepper={true}
              id={formElementId()}
              step={0.01}
              type="number"
              value={z}
              onChange={(e) => onValueChanged(e.target.value, setZ, 'z')}
              onPaste={onPaste}
            />
          )}
        </LabelledContainer>
      )}
    </Stack>
  );
};
